import { CodeBlockPlugin } from '@udecode/plate-code-block/react'
import { getNodeEntries, isType } from '@udecode/plate-common'
import { createPlatePlugin, PlateEditor } from '@udecode/plate-common/react'
import Prism from 'prismjs'

import { isSupportedLanguage, loadPrismLanguage } from '../utils/prismLanguages'
import { BlockType } from '../utils/remark/ast-types'

// WeakMap allows per-editor state with automatic cleanup when editor is GC'd
const editorAttemptedLanguages = new WeakMap<PlateEditor, Set<string>>()

function getAttemptedLanguages(editor: PlateEditor): Set<string> {
    let attempted = editorAttemptedLanguages.get(editor)
    if (!attempted) {
        attempted = new Set<string>()
        editorAttemptedLanguages.set(editor, attempted)
    }
    return attempted
}

/**
 * Scan code blocks and lazy load any missing Prism languages.
 * Uses per-editor state to track attempted loads, preventing cross-instance pollution.
 * Exported so it can be called from RichTextEditor on mount (for edit mode).
 * @param editor - The Plate editor instance
 * @param forceRedecorate - If true, triggers editor.onChange() even if all languages are already loaded
 */
export const loadMissingLanguages = (editor: PlateEditor, forceRedecorate = false) => {
    const attemptedLanguages = getAttemptedLanguages(editor)

    // Find all code blocks in the editor
    const codeBlocks = Array.from(
        getNodeEntries(editor, {
            at: [],
            match: (n) => isType(editor, n, CodeBlockPlugin.key),
        }),
    )

    let hasCodeBlocksWithLang = false

    for (const [node] of codeBlocks) {
        const lang = (node as BlockType).lang
        if (!lang) {
            continue
        }
        if (!isSupportedLanguage(lang)) {
            continue
        }

        hasCodeBlocksWithLang = true

        // Skip if already loaded
        if (Prism.languages[lang]) {
            continue
        }
        if (attemptedLanguages.has(lang)) {
            continue
        }

        // Mark as attempted to avoid duplicate loads
        attemptedLanguages.add(lang)

        // Load the language and trigger re-render
        loadPrismLanguage(lang).then((loaded) => {
            if (loaded) {
                // Force editor to re-decorate by triggering a change
                editor.onChange()
            }
        })
    }

    // If forceRedecorate is true and we have code blocks with languages,
    // trigger onChange to apply decorations (for edit mode where languages are already loaded)
    if (forceRedecorate && hasCodeBlocksWithLang) {
        editor.onChange()
    }
}

/**
 * Plugin that lazy loads Prism languages when code blocks are detected.
 * After loading a language, it triggers a re-render to apply syntax highlighting.
 */
export const LazyPrismPlugin = createPlatePlugin({
    key: 'lazyPrism',
    handlers: {
        onChange: ({ editor }) => {
            loadMissingLanguages(editor)
        },
        // Also check on focus for when editing existing messages
        onFocus: ({ editor }) => {
            loadMissingLanguages(editor)
        },
    },
})
